import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, Info } from 'lucide-react';
import {
  buildCommandCenterBlockers,
  COMMAND_CENTER_BLOCKER_CATEGORY_LABELS,
  groupCommandCenterBlockersByCategory,
  pickTopCommandCenterBlocker,
  type CommandCenterBlocker,
  type CommandCenterBlockerCategory,
  type CommandCenterBlockerContext,
  type CommandCenterBlockerSeverity,
} from '../../commandCenterBlockers';

export interface CommandCenterBlockersPanelProps extends CommandCenterBlockerContext {
  loading?: boolean;
}

const CATEGORY_ORDER: CommandCenterBlockerCategory[] = [
  'billing_access',
  'launch',
  'automation',
  'review',
  'waiting',
  'healthy',
];

const SEVERITY_STYLES: Record<
  CommandCenterBlockerSeverity,
  { border: string; bg: string; icon: React.ReactNode }
> = {
  blocking: {
    border: 'border-red-700/60',
    bg: 'bg-red-950/20',
    icon: <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" aria-hidden />,
  },
  action_required: {
    border: 'border-amber-700/60',
    bg: 'bg-amber-950/20',
    icon: <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" aria-hidden />,
  },
  waiting: {
    border: 'border-secondary-600/60',
    bg: 'bg-secondary-900/40',
    icon: <Clock className="h-4 w-4 text-secondary-300 shrink-0" aria-hidden />,
  },
  warning: {
    border: 'border-amber-700/40',
    bg: 'bg-amber-950/10',
    icon: <Info className="h-4 w-4 text-amber-300 shrink-0" aria-hidden />,
  },
  healthy: {
    border: 'border-emerald-700/50',
    bg: 'bg-emerald-950/20',
    icon: <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" aria-hidden />,
  },
  optional: {
    border: 'border-secondary-700/50',
    bg: 'bg-secondary-900/30',
    icon: <Info className="h-4 w-4 text-secondary-400 shrink-0" aria-hidden />,
  },
};

function BlockerRow({ blocker }: { blocker: CommandCenterBlocker }) {
  const style = SEVERITY_STYLES[blocker.severity];

  return (
    <article
      className={`rounded-lg border ${style.border} ${style.bg} px-4 py-3 space-y-2`}
      data-testid={`command-center-blocker-${blocker.id}`}
      data-blocker-category={blocker.category}
      data-blocker-severity={blocker.severity}
    >
      <div className="flex items-start gap-3">
        {style.icon}
        <div className="min-w-0 space-y-1">
          <h4 className="text-sm font-medium text-white">{blocker.title}</h4>
          <p className="text-sm text-secondary-300" data-testid="command-center-blocker-description">
            {blocker.description}
          </p>
          {blocker.isSystemWaiting ? (
            <p className="text-xs text-secondary-500" data-testid="command-center-blocker-waiting-note">
              No commissioner action needed while LSC waits on Twitch.
            </p>
          ) : null}
          {blocker.requiresCommissioner && !blocker.isSystemWaiting ? (
            <p className="text-xs text-secondary-500" data-testid="command-center-blocker-permission-note">
              Ask a commissioner if you need help with this step.
            </p>
          ) : null}
        </div>
      </div>
      {blocker.primaryActionHref && blocker.primaryActionLabel ? (
        <Link
          to={blocker.primaryActionHref}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-300 hover:text-primary-200"
          data-testid="command-center-blocker-action"
        >
          {blocker.primaryActionLabel}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      ) : blocker.primaryActionLabel && !blocker.isSystemWaiting ? (
        <p className="text-sm text-secondary-400" data-testid="command-center-blocker-action-label">
          {blocker.primaryActionLabel}
        </p>
      ) : null}
    </article>
  );
}

const CommandCenterBlockersPanel: React.FC<CommandCenterBlockersPanelProps> = ({
  loading,
  leagueId,
  readiness,
  activation,
  billingStatus,
  syncStatus,
  digest,
  handoffSessions,
  authError,
  isOwner,
  isOperator,
}) => {
  const blockerContext = useMemo(
    () => ({
      leagueId,
      readiness,
      activation,
      billingStatus,
      syncStatus,
      digest,
      handoffSessions,
      authError,
      isOwner,
      isOperator,
    }),
    [
      leagueId,
      readiness,
      activation,
      billingStatus,
      syncStatus,
      digest,
      handoffSessions,
      authError,
      isOwner,
      isOperator,
    ]
  );

  const blockers = useMemo(() => buildCommandCenterBlockers(blockerContext), [blockerContext]);
  const topBlocker = useMemo(() => pickTopCommandCenterBlocker(blockers), [blockers]);
  const grouped = useMemo(() => groupCommandCenterBlockersByCategory(blockers), [blockers]);

  if (loading) {
    return (
      <section className="card space-y-3" data-testid="command-center-blockers-panel-loading">
        <p className="text-sm text-secondary-400">Loading Command Center status…</p>
      </section>
    );
  }

  const unresolvedCategories = CATEGORY_ORDER.filter(
    (category) => (grouped[category]?.length ?? 0) > 0
  );

  return (
    <section className="card space-y-5" data-testid="command-center-blockers-panel">
      <div>
        <h2 className="text-lg font-semibold text-white">Command Center status</h2>
        <p className="text-sm text-secondary-400 mt-1">
          What is blocking launch, what needs your review, and what LSC is waiting on.
        </p>
      </div>

      {topBlocker && topBlocker.category !== 'healthy' ? (
        <div
          className="rounded-lg border border-primary-700/50 bg-primary-950/20 p-4 space-y-3"
          data-testid="command-center-top-next-action"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-300">Top next action</p>
          <BlockerRow blocker={topBlocker} />
        </div>
      ) : null}

      {unresolvedCategories.map((category) => {
        const items = grouped[category] ?? [];
        if (items.length === 0) return null;
        const showCategoryHeading = category !== 'healthy' || items.length > 0;
        return (
          <div key={category} className="space-y-3" data-testid={`command-center-blocker-group-${category}`}>
            {showCategoryHeading ? (
              <h3 className="text-sm font-semibold text-secondary-200">
                {COMMAND_CENTER_BLOCKER_CATEGORY_LABELS[category]}
              </h3>
            ) : null}
            <div className="space-y-3">
              {items
                .filter((blocker) => blocker.id !== topBlocker?.id || category === 'healthy')
                .map((blocker) => (
                  <BlockerRow key={blocker.id} blocker={blocker} />
                ))}
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default CommandCenterBlockersPanel;
